import { useCallback, useEffect, useState } from "react";
import { makeStyles } from "tss-react/mui";
import { MiModalConfirm, MiScheduler } from "@miplatform/components";
import {
  COLORS,
  EMAIL_TYPE_TO_MODAL_MAP,
  MILLISECONDS_PER_SECOND,
  ORDER_TYPES,
  PERMISSIONS,
  PROPERTY_TYPE,
  SNAP_INTERVAL_IN_HOURS,
  VIEWS,
} from "@miplatform/constants";
import { mdiEmailOutline } from "@mdi/js";
import { usePermissions } from "@mibuildweb/hooks";
import { Box, keyframes } from "@mui/material";
import { AutoLockModal } from "./AutoLockModal/AutoLockModal";
import { BoardModal } from "./BoardModal/BoardModal";
import { BoardsHeader } from "./BoardsHeader/BoardsHeader";
import { CreateNewOrderDrawer } from "./CreateNewOrderDrawer/CreateNewOrderDrawer";
import { EmptyUser } from "./EmptyUser/EmptyUser";
import { ListView } from "./ListView/ListView";
import { DispatchMap } from "./Map/DispatchMap";
import { TechnicianManagementDrawer } from "./TechnicianManagementDrawer/TechnicianManagementDrawer";
import { TechnicianSelectorDrawer } from "./TechnicianSelectorDrawer/TechnicianSelectorDrawer";
import { UserDetails } from "./UserDetails/UserDetails";
import { ViewConfigProvider } from "./ViewConfigContext";
import { SCHEDULE_VIEWS } from "./scheduleViews";

// FIX:ME investigate why webpack is going crazy when conditionally rendering this component
function Calendar(props) {
  return <MiScheduler {...props} />;
}

export const DispatchScheduler = ({
  date,
  time,
  buckets,
  dateRange,
  TaskCardPlus,
  scheduleView,
  calendarView,
  config,
  viewConfig,
  closeoutDrawersMap,
  selectedBoard,
  users,
  displayData,
  selectedTechnician,
  onSelectedTechnicianChange,
  data,
  dateFilterItem,
  emailModal,
  isCreateNewOrderDrawerOpen,
  isTechnicianDrawerOpen,
  boardUsers,
  selectedUserId,
  carouselMode,
  carouselInterval,
  isCarouselPaused,
  attemptsLoading,
  isBoardModalOpen,
  MODALS,
  modal,
  handleBoardModalClose,
  updateBoardConfig,
  closeModal,
  handleAutoLockAction,
  hasModalOpen,
  onAttemptUpdated,
  updateUsersOnBoard,
  handleOpenCloseoutDrawer,
  handleEmailDrawerOpen,
  handleCloseCreateNewOrderDrawer,
  handleEmailModalClose,
  onCardHover,
  updateTime,
  loadSchedulerConfig,
  loadAppointments,
  processData,
  onChangePrevBoard,
  onChangeNextBoard,
  setCarouselInterval,
  toggleCarouselMode,
  setSelectedUserId,
  handleBoardModalOpen,
  setSelectedBoard,
  handleTechnicianDrawerClose,
  setIsTechnicianDrawerOpen,
  onEventUpdate,
  onEventCreate,
  onColumnHeaderClick,
  onDrag,
  loadUsers,
}) => {
  const { classes, theme } = useStyles();
  const { hasPermission } = usePermissions();

  const hasReadCalendarBoardPermission = hasPermission(
    PERMISSIONS.CALENDAR_BOARDS.READ
  );
  const [isTechnicianSelectorOpen, setIsTechnicianSelectorOpen] =
    useState(false);
  const [zoom, setZoom] = useState(1);

  const snappedTime = Math.floor(time.hour / SNAP_INTERVAL_IN_HOURS);

  useEffect(() => {
    loadSchedulerConfig();
  }, [viewConfig?.modifiedOn, viewConfig?.displayBoards]);

  useEffect(() => {
    loadAppointments(calendarView);
    loadUsers(viewConfig);
  }, [dateFilterItem, calendarView, selectedBoard, selectedTechnician]);

  useEffect(() => {
    const intervalId = setInterval(() => {
      updateTime();
    }, MILLISECONDS_PER_SECOND);

    return () => {
      clearInterval(intervalId);
    };
  }, []);

  useEffect(() => {
    processData();
  }, [snappedTime, data, users]);

  const getEventClassName = useCallback(
    (event) => {
      if (
        (closeoutDrawersMap[event?.id]?.wasMinimized &&
          !closeoutDrawersMap[event?.id]?.isOpen) ||
        event.isUnscheduled
      ) {
        return classes.blinkAnimation;
      }

      return null;
    },
    [closeoutDrawersMap]
  );

  return (
    <ViewConfigProvider viewConfig={viewConfig}>
      <Box className={classes.viewContainer}>
        {calendarView !== VIEWS.DAYS && (
          <TechnicianManagementDrawer
            open={isTechnicianDrawerOpen}
            handleClose={handleTechnicianDrawerClose}
            handleBoardModalOpen={handleBoardModalOpen}
            updateUsersOnBoard={updateUsersOnBoard}
            selectedBoard={selectedBoard}
            users={users}
            selectedUserId={selectedUserId}
            setSelectedUserId={setSelectedUserId}
            boardUsers={boardUsers}
            selectedTechnician={selectedTechnician}
            hasModalOpen={hasModalOpen}
            eventClassName={getEventClassName}
            attemptsLoading={attemptsLoading}
            dateRange={dateRange}
            ColumnHeader={UserDetails}
            EventCard={TaskCardPlus}
            onColumnHeaderClick={onColumnHeaderClick}
            onColumnHeaderRefresh={loadSchedulerConfig}
            onEventCreate={onEventCreate}
            onEventUpdate={onEventUpdate}
            onDrag={onDrag}
            onCardHover={onCardHover}
            zoom={zoom}
          />
        )}
        {calendarView === VIEWS.DAYS && (
          <TechnicianSelectorDrawer
            users={users}
            selectedUserId={selectedTechnician?.id}
            onUserSelect={onSelectedTechnicianChange}
            open={isTechnicianSelectorOpen}
            onClose={() => setIsTechnicianSelectorOpen(false)}
          />
        )}
        <Box className={classes.scheduleViews}>
          {hasReadCalendarBoardPermission && (
            <BoardsHeader
              calendarView={calendarView}
              boards={viewConfig?.displayBoards || []}
              onChangePrevBoard={onChangePrevBoard}
              onChangeNextBoard={onChangeNextBoard}
              isCarouselPaused={isCarouselPaused}
              handleBoardModalOpen={handleBoardModalOpen}
              isTechnicianDrawerOpen={isTechnicianDrawerOpen}
              setIsTechnicianDrawerOpen={setIsTechnicianDrawerOpen}
              isTechnicianSelectorOpen={isTechnicianSelectorOpen}
              toggleTechnicianSelectorDrawer={() =>
                setIsTechnicianSelectorOpen((state) => !state)
              }
              selectedBoard={selectedBoard}
              setSelectedBoard={setSelectedBoard}
              carouselMode={carouselMode}
              carouselInterval={carouselInterval}
              setCarouselInterval={setCarouselInterval}
              toggleCarouselMode={toggleCarouselMode}
              selectedTechnician={selectedTechnician}
              onReturn={onColumnHeaderClick}
              zoom={zoom}
              setZoom={setZoom}
            />
          )}
          {scheduleView === SCHEDULE_VIEWS.CALENDAR && (
            <Calendar
              config={config}
              data={displayData}
              buckets={buckets}
              users={boardUsers}
              EmptyUser={EmptyUser}
              view={calendarView}
              date={date}
              time={time}
              selectedUserId={selectedUserId}
              setSelectedUserId={setSelectedUserId}
              selectedTechnician={selectedTechnician}
              hasModalOpen={hasModalOpen}
              eventClassName={getEventClassName}
              attemptsLoading={attemptsLoading}
              dateRange={dateRange}
              ColumnHeader={UserDetails}
              EventCard={TaskCardPlus}
              onColumnHeaderClick={onColumnHeaderClick}
              onColumnHeaderRefresh={loadSchedulerConfig}
              onEventCreate={onEventCreate}
              onEventUpdate={onEventUpdate}
              onDrag={onDrag}
              onCardHover={onCardHover}
              zoom={zoom}
            />
          )}
          {scheduleView === SCHEDULE_VIEWS.MAP && (
            <DispatchMap
              config={config}
              data={displayData}
              date={date}
              users={boardUsers}
              UserCard={UserDetails}
              serviceAreas={viewConfig.serviceAreas}
              onCloseoutDrawerOpen={handleOpenCloseoutDrawer}
              onEmailDrawerOpen={handleEmailDrawerOpen}
            />
          )}

          {scheduleView === SCHEDULE_VIEWS.LIST && (
            <ListView
              data={displayData}
              users={boardUsers}
              EmptyUser={EmptyUser}
              onAttemptUpdated={onAttemptUpdated}
              handleOpenCloseoutDrawer={handleOpenCloseoutDrawer}
            />
          )}
        </Box>
        <CreateNewOrderDrawer
          open={isCreateNewOrderDrawerOpen}
          onClose={handleCloseCreateNewOrderDrawer}
          defaultActiveTab={ORDER_TYPES.SERVICE}
          defaultPropertyType={PROPERTY_TYPE.SERVICE}
        />
        <MiModalConfirm
          title={EMAIL_TYPE_TO_MODAL_MAP[emailModal?.type]?.title}
          titleIcon={{
            path: mdiEmailOutline,
            color: theme.palette.primary.main,
          }}
          textPositive={EMAIL_TYPE_TO_MODAL_MAP[emailModal?.type]?.action}
          open={emailModal}
          handleNegative={handleEmailModalClose}
          handlePositive={() => {
            handleEmailDrawerOpen(emailModal);
            handleEmailModalClose();
          }}
          description={EMAIL_TYPE_TO_MODAL_MAP[emailModal?.type]?.description}
          buttonPositiveColor={COLORS.PRIMARY}
        />
        <BoardModal
          open={isBoardModalOpen}
          handleClose={handleBoardModalClose}
          updateBoardConfig={updateBoardConfig}
          selectedBoard={selectedBoard}
          viewConfig={viewConfig}
        />
        <AutoLockModal
          open={[
            MODALS.AUTO_LOCK_RESCHEDULE,
            MODALS.AUTO_LOCK_SCHEDULE,
          ].includes(modal.type)}
          closeModal={closeModal}
          confirmAction={handleAutoLockAction}
          isReschedule={modal.type === MODALS.AUTO_LOCK_RESCHEDULE}
        />
      </Box>
    </ViewConfigProvider>
  );
};

const useStyles = makeStyles()((theme) => {
  const blinkAnimation = keyframes`
    0% {
      box-shadow: 0 0 0 0 ${theme.palette.orange.mediumLight};
    }
    50% {
      box-shadow: 0 0 5px 4px ${theme.palette.orange.mediumLight};
    }
    100% {
      box-shadow: 0 0 0 0 ${theme.palette.orange.mediumLight};
    }
  `;

  return {
    viewContainer: {
      flex: 1,
      display: "flex",
      overflow: "hidden",
    },
    scheduleViews: {
      display: "flex",
      flexDirection: "column",
      overflow: "hidden",
      flex: 1,
      paddingTop: theme.spacing(1),
    },
    blinkAnimation: {
      animation: `${blinkAnimation} 800ms infinite`,
    },
  };
});
